import type { RuleModule } from '../engine/registry';
import type { GameEventDraft, GameRuntime, GameState, Placement } from '../engine/state';
import type { ResourceBag } from '../engine/gameDef';
import type { VertexId } from '../board/axial';
import { banditHexOf } from './banditRead';
import { enterPhase } from '../engine/phase';
import { liveBoard } from '../engine/liveBoard';
import type { BoardGraph } from '../board/graph';

// Harvest (outline §6; plan §8.2): every hex whose disc matches the roll pays every
// adjacent building its tile's resource — yield per piece from module config (content-
// blind engine; the base game's settlement 1 / city 2 live HERE). Mandatory + automatic
// inside ROLL (outline §14.8 — no decline, nothing interposes). The Bandit suppresses
// ONLY its own hex (outline §14.10; soft-read so removing the bandit module just removes
// suppression). Supply shortage per type per roll (outline §6/§14.9): one claimant takes
// min(owed, stock); two+ claimants ⇒ that type pays nobody; types independent.

export const HARVEST_REJECTS = {} as const;

/** The module's resolution core — exported so fixtures drive exact sums (T-ENG-007..013). */
export function resolveHarvest(state: GameState, env: GameRuntime, sum: number, emit: (e: GameEventDraft) => void): void {
  const yields = (env.composed.moduleConfig['harvest']?.['yields'] ?? {}) as Record<string, number>;
  const producesOf = new Map(env.def.tiles.map((t) => [t.id, t.produces]));
  const banditHex = banditHexOf(state);
  const board = liveBoard(state, env); // H20: a hex revealed from fog pays like any other

  // owed[resource][seat] accumulated over matching hexes (deterministic map iteration:
  // resource order from the def; seats ascending)
  const owed = new Map<string, Map<number, number>>();
  for (const hex of board.hexes.values()) {
    if (hex.disc !== sum) continue;
    if (hex.id === banditHex) continue; // outline §14.10: production only
    const resource = producesOf.get(hex.tile) ?? null;
    if (resource === null) continue; // desert-class tiles never pay
    for (const [vid, placement] of Object.entries(state.board.vertices)) {
      if (placement.owner === 'neutral') continue; // blockers never produce (outline §13.1)
      const y = yields[placement.piece] ?? 0;
      if (y === 0) continue;
      if (!board.vertices.get(vid)?.hexes.includes(hex.id)) continue;
      const bySeat = owed.get(resource) ?? new Map<number, number>();
      bySeat.set(placement.owner, (bySeat.get(placement.owner) ?? 0) + y);
      owed.set(resource, bySeat);
    }
  }

  const paidBySeat = new Map<number, ResourceBag>();
  for (const r of env.def.resources) {
    const bySeat = owed.get(r.id);
    if (!bySeat) continue;
    const claimants = [...bySeat.keys()].sort((a, b) => a - b);
    const totalOwed = claimants.reduce((n, s) => n + bySeat.get(s)!, 0);
    const stock = state.supply[r.id] ?? 0;
    if (totalOwed <= stock) {
      for (const seat of claimants) {
        const n = bySeat.get(seat)!;
        state.supply[r.id] = (state.supply[r.id] ?? 0) - n;
        state.seats[seat]!.hand[r.id] = (state.seats[seat]!.hand[r.id] ?? 0) + n;
        const bag = paidBySeat.get(seat) ?? {};
        bag[r.id] = (bag[r.id] ?? 0) + n;
        paidBySeat.set(seat, bag);
      }
    } else if (claimants.length === 1) {
      // exactly one claimant: they take whatever remains (outline §6)
      const seat = claimants[0]!;
      const grant = Math.min(bySeat.get(seat)!, stock);
      if (grant > 0) {
        state.supply[r.id] = stock - grant;
        state.seats[seat]!.hand[r.id] = (state.seats[seat]!.hand[r.id] ?? 0) + grant;
        const bag = paidBySeat.get(seat) ?? {};
        bag[r.id] = (bag[r.id] ?? 0) + grant;
        paidBySeat.set(seat, bag);
      }
      emit({ kind: 'SUPPLY_SHORT', vis: 'public', resource: r.id, mode: 'partial', claimants });
    } else {
      // two or more claimants: NOBODY receives this type this roll (outline §14.9)
      emit({ kind: 'SUPPLY_SHORT', vis: 'public', resource: r.id, mode: 'nobody', claimants });
    }
  }

  for (const [seat, bag] of [...paidBySeat.entries()].sort((a, b) => a[0] - b[0])) {
    emit({ kind: 'HARVEST_PAID', vis: 'public', seat, bag, sum });
  }
}

/**
 * Each seat's PRODUCTION SCORE (D253, the user's definition): for every building, the pips of
 * each producing hex it touches, times what that building yields — so two settlements on a 5
 * (four pips) are worth 8, and a single city there is worth 8 too. It is exactly the resources
 * the harvest would pay a seat over one of every roll, which is why it reads the SAME tables
 * as `resolveHarvest` above: yields per piece from this module's config, pips from
 * `cfg.engine.discPips`, desert-class tiles and neutral blockers paying nothing.
 *
 * The Bandit is not subtracted here: it moves every few turns, and the total describes the
 * land a seat has built on. What it is blocking THIS minute is `hexProduction` of its hex —
 * the user's grey "(−6)" beside the total — computed by the same loop, so the two can never
 * disagree about what a hex is worth.
 */
export function productionScores(vertices: Readonly<Record<VertexId, Placement>>, env: GameRuntime, board: BoardGraph = env.board): number[] {
  return scoreBuildings(vertices, env, () => true, board);
}

/** The share of every seat's production score that ONE hex supplies — what blocking it costs. */
export function hexProduction(vertices: Readonly<Record<VertexId, Placement>>, env: GameRuntime, hex: string, board: BoardGraph = env.board): number[] {
  return scoreBuildings(vertices, env, (hid) => hid === hex, board);
}

/**
 * H20: `board` is the graph to score on — pass the LIVE board wherever fog may have lifted (a
 * client passes `liveBoard(stateFromView(view, env), env)`); it defaults to the static board,
 * which IS the live one in every classic game. A numbered tile that produces no FIXED resource
 * (a gold field) pays what the map's `sea.rules.goldYield` says per building — one card of the
 * owner's choice per unit — so it counts at that yield (T-SEA-020 proves the parity against the
 * real harvest). No classic map has one, so a classic score is exactly what it was.
 */
function scoreBuildings(vertices: Readonly<Record<VertexId, Placement>>, env: GameRuntime, counts: (hex: string) => boolean, board: BoardGraph): number[] {
  const yields = (env.composed.moduleConfig['harvest']?.['yields'] ?? {}) as Record<string, number>;
  const choiceYields = (env.map.sea?.rules.goldYield ?? {}) as Record<string, number>;
  const tileOf = new Map(env.def.tiles.map((t) => [t.id, t]));
  const scores = new Array<number>(env.seatsCount).fill(0);
  for (const [vid, placement] of Object.entries(vertices)) {
    if (placement.owner === 'neutral') continue; // blockers never produce (outline §13.1)
    let score = 0;
    for (const hid of board.vertices.get(vid)?.hexes ?? []) {
      if (!counts(hid)) continue;
      const hex = board.hexes.get(hid);
      const tile = hex ? tileOf.get(hex.tile) : undefined;
      if (!hex || !tile || hex.disc === null) continue;
      const y = tile.produces !== null ? (yields[placement.piece] ?? 0) : tile.numbered === true ? (choiceYields[placement.piece] ?? 0) : 0;
      score += y * (env.cfg.engine.discPips[hex.disc] ?? 0);
    }
    scores[placement.owner] = (scores[placement.owner] ?? 0) + score;
  }
  return scores;
}

export const harvestModule: RuleModule = {
  id: 'harvest',
  register(ctx) {
    ctx.configDefaults({ yields: { settlement: 1, city: 2 } }); // outline §6 rates, module-owned
    ctx.onDiceResolved((state, env, d1, d2, emit) => {
      const sum = d1 + d2;
      if (sum === 7) return 'pass'; // the bandit module claims 7s (or nothing happens without it)
      resolveHarvest(state, env, sum, emit);
      if (state.phase.t === 'PRE_ROLL') {
        enterPhase(state, env, { t: 'ACTION', seat: state.phase.seat }, emit);
      }
      return 'handled';
    });
  },
};
